import { useState } from "react";
import axios from "axios";
import { SERVICE_OPTIONS } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  company: "",
  service: "",
  message: "",
  website: "", // honeypot — must stay empty
};

const FIELD =
  "w-full px-4 py-2.5 rounded bg-steel border border-slate-600 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-royal transition-colors";
const LABEL = "block text-xs font-mono-sub text-slate-300 mb-1";

export function ConsultationForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [feedback, setFeedback] = useState("");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setFeedback("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus("success");
      setFeedback(res.data.message);
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setFeedback(
        typeof detail === "string"
          ? detail
          : "Something went wrong while sending your inquiry. Please try again.",
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success-message"
        className="bg-steel-light p-8 rounded-xl border border-slate-700 text-center"
      >
        <p className="font-poster font-bold text-xl text-white">Request received</p>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed">{feedback}</p>
        <button
          data-testid="contact-send-another-button"
          onClick={() => {
            setForm(EMPTY);
            setStatus("idle");
          }}
          className="mt-6 px-6 py-2.5 rounded border border-slate-600 text-slate-200 text-sm hover:bg-steel transition-colors"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit}
      className="space-y-4 bg-steel-light p-6 sm:p-8 rounded-xl border border-slate-700"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className={LABEL}>
            NAME *
          </label>
          <input
            id="contact-name"
            data-testid="contact-name-input"
            type="text"
            name="name"
            required
            minLength={2}
            maxLength={120}
            value={form.name}
            onChange={update}
            className={FIELD}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={LABEL}>
            BUSINESS EMAIL *
          </label>
          <input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            name="email"
            required
            value={form.email}
            onChange={update}
            className={FIELD}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-company" className={LABEL}>
            COMPANY
          </label>
          <input
            id="contact-company"
            data-testid="contact-company-input"
            type="text"
            name="company"
            maxLength={160}
            value={form.company}
            onChange={update}
            className={FIELD}
          />
        </div>
        <div>
          <label htmlFor="contact-service" className={LABEL}>
            SERVICE NEEDED *
          </label>
          <select
            id="contact-service"
            data-testid="contact-service-select"
            name="service"
            required
            value={form.service}
            onChange={update}
            className={FIELD}
          >
            <option value="">Select Service...</option>
            {SERVICE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="contact-message" className={LABEL}>
          SHORT PROJECT DESCRIPTION *
        </label>
        <textarea
          id="contact-message"
          data-testid="contact-message-input"
          name="message"
          rows={4}
          required
          minLength={10}
          maxLength={2000}
          value={form.message}
          onChange={update}
          className={FIELD}
        />
      </div>

      {/* Honeypot: invisible to humans, attractive to bots. Never fill this. */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={update}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        data-testid="contact-honeypot-input"
      />

      {status === "error" && (
        <p data-testid="contact-error-message" className="text-sm text-red-400 text-center">
          {feedback}
        </p>
      )}

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "submitting"}
        className="w-full py-3.5 rounded bg-royal hover:bg-royal-dark disabled:opacity-60 text-white font-bold text-sm transition-colors shadow-md"
      >
        {status === "submitting" ? "Sending…" : "Request a Consultation"}
      </button>
    </form>
  );
}
